import { useEffect, useState } from "react";
import { Card, Checkbox, Cluster, Input, Stack } from "@salesapp/ui";
import { opsApi, type WorkflowRule } from "../../lib/opsApi";
import { useAccess } from "../../shell/AccessProvider";
import { FIELD_HINTS } from "../../help/fieldHints";
import { CompanyLifecycleSettings } from "./CompanyLifecycleSettings";
import { TabFieldOptionsSettings } from "./TabFieldOptionsSettings";

export function CompanyWorkflowSettings({ orgId }: { orgId: string }) {
  const { can } = useAccess();
  const canWorkflows = can("crm.workflows", "configure");
  const canAging = can("crm.opportunities.aging", "configure");
  const canConfigureOptions = can("crm.field_options", "configure");
  const [rules, setRules] = useState<WorkflowRule[]>([]);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    try {
      setRules((await opsApi.listWorkflows(orgId)).rules);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Rules could not be loaded.");
    }
  }

  useEffect(() => {
    void load();
  }, [orgId]);

  const visible = rules.filter((rule) => rule.entity !== "opportunity" || canAging || canWorkflows);
  const lockedAging = rules.filter((rule) => rule.entity === "opportunity");

  return (
    <Stack gap={4}>
      <CompanyLifecycleSettings orgId={orgId} />
      {error && <p className="crm-error">{error}</p>}
      {canConfigureOptions ? (
        <Stack gap={2}>
          <h2 className="crm-section-title">Company dropdown options</h2>
          <TabFieldOptionsSettings orgId={orgId} entity="company" />
        </Stack>
      ) : null}
      <p className="home-subtitle">
        Default company and lead reminders run on every plan. Configurable automation and opportunity aging are Professional features.
      </p>
      {!canWorkflows && !canAging ? (
        <p className="home-subtitle">Upgrade to Professional to change reminder intervals and enable stalled-deal aging.</p>
      ) : null}
      {visible.filter((rule) => rule.entity !== "opportunity").map((rule) => (
        <Card key={rule.id}>
          <Cluster gap={3} align="center">
            <strong>{rule.name}</strong>
            <span className="crm-muted">{rule.kind} · every</span>
            <Input
              type="number"
              tooltip={FIELD_HINTS.workflow_interval_days}
              value={String(rule.interval_days ?? "")}
              disabled={!canWorkflows}
              onChange={(event) => {
                if (!rule.id || !canWorkflows) return;
                const next = Number(event.target.value);
                if (!Number.isFinite(next) || next < 1) return;
                void opsApi.patchWorkflow(orgId, rule.id, { interval_days: next }).then(load);
              }}
            />
            <span className="crm-muted">days</span>
            <Checkbox
              checked={Boolean(rule.enabled)}
              disabled={!canWorkflows}
              onChange={(event) => {
                if (!rule.id || !canWorkflows) return;
                void opsApi.patchWorkflow(orgId, rule.id, { enabled: event.target.checked }).then(load);
              }}
              label="Enabled"
              tooltip={FIELD_HINTS.workflow_enabled}
            />
          </Cluster>
        </Card>
      ))}
      {canAging
        ? lockedAging.map((rule) => (
            <Card key={rule.id}>
              <Cluster gap={3} align="center">
                <strong>{rule.name}</strong>
                <span className="crm-muted">{rule.kind} · every</span>
                <Input
                  type="number"
                  tooltip={FIELD_HINTS.workflow_interval_days}
                  value={String(rule.interval_days ?? "")}
                  onChange={(event) => {
                    if (!rule.id) return;
                    const next = Number(event.target.value);
                    if (!Number.isFinite(next) || next < 1) return;
                    void opsApi.patchWorkflow(orgId, rule.id, { interval_days: next }).then(load);
                  }}
                />
                <span className="crm-muted">days</span>
                <Checkbox
                  checked={Boolean(rule.enabled)}
                  onChange={(event) => {
                    if (!rule.id) return;
                    void opsApi.patchWorkflow(orgId, rule.id, { enabled: event.target.checked }).then(load);
                  }}
                  label="Enabled"
                  tooltip={FIELD_HINTS.workflow_enabled}
                />
              </Cluster>
            </Card>
          ))
        : (
          <Card>
            <p className="home-subtitle">
              Opportunity aging (stalled-deal alerts on the pipeline) is included with Professional.
            </p>
          </Card>
        )}
    </Stack>
  );
}
